import React from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  Code
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { isMachineCodingEnabledFor } from '../../config/featureAccess';

const Footer: React.FC = () => {
  const { state: authState } = useAuth();
  const machineCodingEnabled = isMachineCodingEnabledFor(authState.user?.enabledFeatures);
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Explore',
      links: [
        { label: 'System Design', to: '/system-design' },
        { label: 'Languages', to: '/languages' },
        { label: 'Practice', to: '/practice' },
      ],
    },
    {
      title: 'Practice',
      links: [
        { label: 'DSA Problems', to: '/practice/dsa' },
        { label: 'Machine Coding', to: '/practice/machine-coding' },
        { label: 'LLD Practice', to: '/practice/lld' },
        { label: 'HLD System Design', to: '/practice/hld' },
      ],
    },
    {
      title: 'Legal',
      links: [
        { label: 'Contact', to: '/contact' },
        { label: 'Privacy Policy', to: '/privacy' },
        { label: 'Terms of Service', to: '/terms' },
      ],
    },
  ];

  const socialLinks: any[] = [];

  return (
    <footer className="site-footer bg-gray-900 dark:bg-gray-950 text-gray-300 border-t border-gray-800 dark:border-gray-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Main Footer Content - Compact Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6 mb-4">
          {/* Brand Section */}
          <div className="col-span-2">
            <Link to="/" className="flex items-center space-x-2 mb-2">
              <Code className="w-6 h-6 text-primary-500" />
              <span className="text-lg font-bold text-white">LetsBoostUp</span>
            </Link>
            <p className="text-gray-400 text-xs mb-3 max-w-xs">
              Prepare. Practice. Prevail. Master software engineering through comprehensive learning.
            </p>
            {/* Social Links */}
            <div className="flex space-x-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-lg bg-gray-800 dark:bg-gray-800 hover:bg-gray-700 dark:hover:bg-gray-700 flex items-center justify-center transition-colors"
                    aria-label={social.label}
                  >
                    <Icon className="w-4 h-4 text-gray-400 hover:text-white" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Footer Sections - Compact */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h3 className="text-white font-semibold mb-2 text-xs uppercase tracking-wider">
                {section.title}
              </h3>
              <ul className="space-y-1.5">
                {section.links.filter((link) => link.label !== 'Machine Coding' || machineCodingEnabled).map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-gray-400 hover:text-white text-xs transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar - Compact */}
        <div className="border-t border-gray-800 dark:border-gray-800 pt-4">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-2 md:space-y-0">
            <div className="flex items-center space-x-2 text-gray-400 text-xs">
              <span>© {currentYear} LetsBoostUp. All rights reserved.</span>
            </div>
            <div className="flex items-center space-x-1 text-gray-400 text-xs">
              <span>Made with</span>
              <Heart className="w-3 h-3 text-red-500 fill-current" />
              <span>for learners</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
